import { useEffect, useRef } from "react";
import { useRoom } from "../contexts/RoomContext.jsx";
import { useSound } from "../contexts/SoundContext.jsx";
import { GAME_CATALOG } from "../data/gameCatalog.js";

const GAME_COLORS = {
  "quem-sou-eu": "#6366f1",
  termo: "#10b981",
  "quebra-cabeca": "#f59e0b",
  stop: "#ec4899",
};
const medal = (position) => ["🥇", "🥈", "🥉"][position] || `${position + 1}º`;
const points = (player) => {
  const value = Number(player?.points);
  return Number.isFinite(value) ? value : 0;
};

function GamePerformance({ players, played }) {
  const games = GAME_CATALOG.filter((game) => played?.[game.id]);
  if (games.length < 2) return null;
  const maximum = Math.max(
    1,
    ...players.flatMap((player) =>
      games.map((game) => player.pontosPorJogo?.[game.id] || 0),
    ),
  );

  return (
    <div className="card final-performance">
      <h3 className="section-title">📊 Desempenho por Jogo</h3>
      <p>Veja em qual jogo cada pessoa se saiu melhor:</p>
      <div className="final-legend">
        {games.map((game) => (
          <span key={game.id}>
            <i style={{ background: GAME_COLORS[game.id] }} /> {game.nome}
          </span>
        ))}
      </div>
      <div className="final-chart">
        {players.map((player) => (
          <div className="final-chart-player" key={player.id}>
            <strong>
              {player.username} {player.isHost && "👑"}
            </strong>
            {games.map((game) => {
              const value = player.pontosPorJogo?.[game.id] || 0;
              const width =
                value > 0 ? Math.max((value / maximum) * 100, 3) : 0;
              return (
                <div className="final-chart-row" key={game.id}>
                  <span>{game.nome}</span>
                  <div>
                    <i
                      style={{
                        width: `${width}%`,
                        background: GAME_COLORS[game.id],
                      }}
                    />
                  </div>
                  <b>{value} pts</b>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

export function FinalRankingPage() {
  const { roomData, isHost, resetGame } = useRoom();
  const { tocarSom, mutado } = useSound();
  const victoryAudio = useRef(null);

  useEffect(() => {
    tocarSom(victoryAudio, 0.5);
  }, []);

  if (!roomData?.players)
    return <p className="text-center mt-40">Carregando...</p>;
  const players = Object.values(roomData.players).sort(
    (first, second) =>
      points(second) - points(first) ||
      (first.username || "").localeCompare(second.username || "", "pt-BR", {
        sensitivity: "base",
      }),
  );

  return (
    <>
      <audio
        ref={victoryAudio}
        src="/sons/vitoria.mp3"
        preload="auto"
        muted={mutado}
      />
      <div className="container-sm text-center final-ranking-page">
        <h2 className="title">🏆 Pódio Final</h2>
        <p className="subtitle mb-40">
          A sessão acabou! Veja o resultado de todos os jogos:
        </p>
        <div className="card final-podium">
          <ul className="player-list">
            {players.map((player, index) => (
              <li
                className={`player-item final-position position-${index + 1}`}
                key={player.id}
              >
                <div>
                  <span>{medal(index)}</span>
                  <strong>
                    {player.username} {player.isHost && "👑"}
                  </strong>
                </div>
                <span className="player-points">{points(player)} pts</span>
              </li>
            ))}
          </ul>
        </div>
        <GamePerformance players={players} played={roomData.jogosJogados} />
        <div className="mt-40">
          {isHost ? (
            <button
              className="btn btn-success final-play-again"
              onClick={resetGame}
              type="button"
            >
              🎮 Jogar Novamente
            </button>
          ) : (
            <p className="info-box">
              Aguardando o Host decidir se haverá uma nova partida...
            </p>
          )}
        </div>
      </div>
    </>
  );
}
